// COP — the I5 chat key-access adapter (CONTRACT:I5-KEY-ACCESS.1.0), bridging the
// crypto-agnostic @tak-tdf/cap-chat ChatKeyAccessPort (CONTRACT:C3-CAPABILITY-PLUGIN.1.0,
// packages/cap-chat/src/keyaccess.ts) onto the COP's real OpenTDF KAS (kas.ts).
//
// THE RECONCILIATION (CHAT-BUILD-SPEC §2.2). The port is 2-STEP — rewrap() releases a
// decision (grant→key, deny→reason-only), then open(ref, key) recovers the plaintext —
// but OpenTDF's reader is ONE-SHOT: client.open(source).decrypt() does the rewrap AND the
// decrypt together; there is no released-key-then-decrypt split. We honor the port's
// contract (DENY CARRIES NO KEY AND NO PLAINTEXT) WITHOUT leaking on deny by:
//   - rewrap() runs the one-shot decryptTdf(); ON GRANT we cache the recovered plaintext
//     keyed by encryptedRef in a PRIVATE Map and return an OPAQUE, non-cryptographic
//     handle (NOT key material — the real DEK never crosses the port boundary).
//   - ON DENY we cache NOTHING and return { granted:false, reason } only.
//   - open(ref, handle) returns the cached plaintext for ref, or "" if absent.
// A deny can NEVER be coerced into plaintext because the cache is populated ONLY on the
// grant branch of rewrap — open() has nothing to hand back for a ref that was denied.
//
// IDENTITY-SWITCH DISCIPLINE (fail-closed): the cache is cleared on EVERY persona switch
// (registered against kas.ts onIdentitySwitch, fired by setKasToken), so a new — possibly
// LOWER — persona re-decides every ref through a fresh rewrap and never inherits the prior
// persona's opened bodies. Same discipline as the room-store rebuild (CHAT-BUILD-SPEC §4.3).

import type { PolicyBundle } from "../../../../packages/abac-pdp/src/index.ts";
import type {
  ChatKeyAccessPort,
  RewrapResult,
  SealResult,
  SubjectAttrs,
} from "../../../../packages/cap-chat/src/keyaccess.ts";
import { roomLabels } from "../../../../packages/cap-chat/src/messages.ts";
import { onIdentitySwitch, sealTdf, decryptTdf, type ProtectLabels } from "./kas.ts";

/** The one-shot TDF primitives this adapter rides on. Abstracted so a T0 test can inject
 *  a STUB reader (no KAS, no network) while production injects the real kas.ts funcs. */
export interface TdfCrypto {
  /** Produce side: seal a body as a real TDF under the room's markings (kas.ts:sealTdf).
   *  `expectedNamespace` (FIX 3) lets the adapter pin the bundle to the live namespace. */
  seal(text: string, labels: ProtectLabels, bundle: PolicyBundle, expectedNamespace?: string): Promise<string>;
  /** Read side: the one-shot rewrap+decrypt (kas.ts:decryptTdf). Grant → plaintext;
   *  deny/error → { granted:false, reason } (fails closed, the KAS is authoritative). */
  decrypt(ref: string): Promise<{ granted: boolean; plaintext?: string; reason?: string }>;
}

/** Live wiring: the real kas.ts primitives (the only place the COP touches crypto). */
const liveCrypto: TdfCrypto = { seal: sealTdf, decrypt: decryptTdf };

/** The COP-side ChatKeyAccessPort, plus the lifecycle hooks the host wires:
 *  - clearPlaintextCache(): drop every opened body (called on identity switch).
 *  - dispose(): unregister the identity-switch listener (teardown). */
export interface ChatKeyAccess extends ChatKeyAccessPort {
  /** R1 — the port is now ASYNC, so the canonical `seal` (from ChatKeyAccessPort) IS the
   *  real network seal. `sealAsync` is kept as an explicit alias the COP send path / T0
   *  can call by name (sealAsync→seal). It seals the body as a real TDF under the room's
   *  marking; FAILS CLOSED (throws) on any unbound/contradictory/wrong-namespace marking. */
  sealAsync(req: { room: string; body: string; policyRef: string; classification?: string; releasability?: string[] }): Promise<SealResult>;
  /** R1 — `rewrap` (from ChatKeyAccessPort) IS the real async one-shot rewrap+decrypt:
   *  ON GRANT caches the recovered plaintext + returns an opaque handle; ON DENY caches
   *  NOTHING + returns a reason only (fresh decision every call — CHAT-BUILD-SPEC §2.2).
   *  `rewrapAsync` is an explicit alias (rewrapAsync→rewrap). The COP read path awaits
   *  rewrap, then awaits open() to recover the body ONLY on grant. */
  rewrapAsync(req: { encryptedRef: string; subject: SubjectAttrs }): Promise<RewrapResult>;
  /** Drop every opened plaintext (fail-closed). Idempotent. */
  clearPlaintextCache(): void;
  /** Unregister the identity-switch listener. */
  dispose(): void;
}

/** Options for the COP chat key-access adapter. */
export interface ChatKeyAccessOptions {
  /** One-shot TDF primitives (defaults to the real kas.ts funcs; a test injects a stub). */
  crypto?: TdfCrypto;
  /** FIX 3 — the LIVE policy namespace the read path/KAS decide over (the COP threads its
   *  /api/config namespace). When set, sealTdf refuses a bundle from a different namespace
   *  (a seed/stale bundle binding a different attribute universe). */
  expectedNamespace?: string;
}

/** Build the COP chat key-access adapter over the live policy bundle.
 *
 *  @param getBundle returns the LIVE policy bundle (the COP's tdflite.policyBundle()),
 *         so the seal binds the SAME attributes the I4 filter decides over (lockstep).
 *  @param opts      crypto primitives + the expected live namespace (FIX 3). */
export function createChatKeyAccess(
  getBundle: () => PolicyBundle,
  opts: ChatKeyAccessOptions = {},
): ChatKeyAccess {
  const crypto = opts.crypto ?? liveCrypto;
  const expectedNamespace = opts.expectedNamespace;

  // The ONLY place a recovered chat body lives. Populated ONLY on the grant branch of
  // rewrap; read ONLY by open; cleared wholesale on identity switch. Never persisted.
  const plaintextCache = new Map<string, string>();

  // FIX 1 — STALE-GRANT RACE epoch. Bumped on EVERY cache clear (every setKasToken). A
  // rewrapAsync captures the epoch at ENTRY; if the epoch changed while its decrypt was
  // in flight (an identity switch fired), the late grant is DROPPED (not cached) and
  // returns a stale-deny — so a HIGH grant resolving after a switch-to-LOW can never write
  // HIGH's plaintext into LOW's (cleared) cache. The grant is tied to the identity that
  // initiated it.
  let epoch = 0;

  /** An OPAQUE lookup handle for open() — NOT key material. It is the ref bytes; the real
   *  DEK never leaves OpenTDF. open() ignores the handle's value beyond the cache lookup,
   *  so a forged handle can never recover a denied (uncached) body. */
  const openHandle = (encryptedRef: string): Uint8Array => new TextEncoder().encode(encryptedRef);

  const clearPlaintextCache = (): void => {
    plaintextCache.clear();
    epoch++; // FIX 1: invalidate any in-flight grant that started under the prior identity.
  };

  // Fail-closed identity-switch discipline: a new persona inherits NO opened bodies.
  const unregister = onIdentitySwitch(clearPlaintextCache);

  /** Map an outbound body into the room's seal labels (the room marking is the floor, L2).
   *  Threads the creator-set releasability so the SEAL binds the SAME releasability the
   *  envelope's `security` declares (lockstep). */
  const labelsFor = (room: string, classification?: string, releasability?: string[]): ProtectLabels => {
    const sec = roomLabels(room, classification, releasability);
    return {
      classification: sec.classification,
      releasability: sec.releasability,
      mission: sec.mission,
      caveats: sec.caveats,
    };
  };

  // R1 — the canonical ASYNC port methods. `seal`/`rewrap`/`open` ARE async (the port
  // is now async), so the COP's send/read path awaits them directly. `sealAsync`/
  // `rewrapAsync` (below) are explicit aliases to the same impls.
  const seal: ChatKeyAccess["seal"] = async ({ room, body, classification, releasability }) => {
    // FAIL CLOSED: sealTdf throws on any failure (unbound/contradictory/wrong-namespace
    // markings included — FIX 2/3/4/5 + R3 mandatory-namespace); we DO NOT swallow it. The
    // caller keeps the draft and surfaces an error — never a silent clear body (§2.1).
    const encryptedRef = await crypto.seal(
      body,
      labelsFor(room, classification, releasability),
      getBundle(),
      expectedNamespace,
    );
    return { encryptedRef };
  };

  // The REAL I5 read decision (async, one-shot). ON GRANT caches the plaintext + returns
  // an opaque handle; ON DENY caches NOTHING + returns reason only (fresh every call).
  const rewrap: ChatKeyAccess["rewrap"] = async (req: { encryptedRef: string; subject: SubjectAttrs }) => {
    // FIX 1 — capture the identity epoch at ENTRY. If it changes while decrypt is in
    // flight (an identity switch fired → cache cleared + epoch bumped), this grant
    // belongs to the OLD persona and must NOT land in the new persona's cache.
    const startEpoch = epoch;
    const outcome = await crypto.decrypt(req.encryptedRef);
    if (outcome.granted) {
      if (epoch !== startEpoch) {
        // The identity switched mid-flight. DROP the plaintext (never cache it) and deny
        // as stale — the new (possibly lower) persona must re-decide via a fresh rewrap.
        // This closes the clear-then-repopulate TOCTOU that would leak HIGH's body to LOW.
        return { granted: false, reason: "STALE_IDENTITY" };
      }
      // GRANT branch — the ONLY place the cache is populated, and only for the CURRENT identity.
      plaintextCache.set(req.encryptedRef, outcome.plaintext ?? "");
      return { granted: true, key: openHandle(req.encryptedRef) };
    }
    // DENY branch — cache NOTHING; carry only the reason. open() can never recover this.
    return { granted: false, reason: outcome.reason ?? "ACCESS_DENIED" };
  };

  return {
    seal,
    // sealAsync is an explicit alias the COP send path / T0 calls by name (R1: sealAsync→seal).
    sealAsync: seal,

    rewrap,
    // rewrapAsync is an explicit alias (R1: rewrapAsync→rewrap).
    rewrapAsync: rewrap,

    async open(encryptedRef: string, _handle: Uint8Array): Promise<string> {
      // Return the cached plaintext for a GRANTED ref, or "" if absent. A deny cached
      // NOTHING, so a denied (or coerced) ref yields "" — never a leak. The handle is a
      // lookup token only; we deliberately ignore its value (a forged handle gets "").
      return plaintextCache.get(encryptedRef) ?? "";
    },

    clearPlaintextCache,
    // FIX 7 — dispose fails CLOSED on teardown: unregister the listener AND wipe the cache
    // (don't leave opened bodies in a Map whose clear-trigger was just removed).
    dispose(): void {
      unregister();
      clearPlaintextCache();
    },
  };
}
